import { FestivalData } from '../../../../model/FestivalData';
import { Messages } from '../../../../i18n/messages';
import { AgentPatch, agentPatchSegments, agentPatchValues, applyAgentPatches } from '../../../../services/festivalAgentService';

export interface AgentChange {
  label: string;
  before: string;
  after: string;
}

export const describeAgentChanges = (snapshot: FestivalData, patches: AgentPatch[], t: Messages<'agent'>, locale: string): AgentChange[] => {
  const result = applyAgentPatches(snapshot, snapshot, patches);
  const artistNames = new Map([...snapshot.artists, ...result.artists].map(artist => [artist.identifier, artist.name]));
  const fields: Record<string, string> = t.fields;
  const sections: Record<string, string> = t.sections;
  const fieldLabel = (field: string) => fields[field] ?? sections[field] ?? field.replace(/_/g, ' ');
  const preview = (value: unknown, field: string): string => {
    if (value === undefined || value === null || value === '') return t.nothing;
    if (typeof value === 'boolean') return value ? t.yes : t.no;
    if (typeof value === 'number') return String(value);
    if (typeof value === 'string') {
      if (field === 'artist_id') return artistNames.get(value) ?? value;
      if (field === 'scene_id') return snapshot.pois.find(poi => poi.identifier === value)?.name ?? value;
      if (field.endsWith('_date') && Number.isFinite(Date.parse(value))) return new Date(value).toLocaleString(locale);
      return value.length > 140 ? `${value.slice(0, 140)}…` : value;
    }
    if (typeof value === 'object') {
      const item = value as Record<string, unknown>;
      if (typeof item.name === 'string') return item.name;
      if (typeof item.artist_id === 'string') return `${preview(item.artist_id, 'artist_id')} · ${preview(item.start_date, 'start_date')}`;
      return Object.entries(item).filter(([key]) => key !== 'image').map(([key, child]) => `${fieldLabel(key)}: ${preview(child, key)}`).join(' · ');
    }
    return t.nothing;
  };
  return agentPatchValues(snapshot, patches).map(({ patch, before, after, context }) => {
    const parts = agentPatchSegments(patch);
    const labels = parts.map((part, index) => {
      if (index === 0) return sections[part] ?? fieldLabel(part);
      if (index === 1 && (parts[0] === 'artists' || parts[0] === 'shows')) {
        if (parts.length === 2) return patch.op === 'remove' ? t.removed : t.added;
        return preview(parts[0] === 'artists' ? context.artists[Number(part)] : context.shows[Number(part)], '');
      }
      return fieldLabel(part);
    });
    const field = parts[parts.length - 1];
    return { label: labels.join(' › '), before: preview(before, field), after: preview(after, field) };
  });
};
